<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  nextTick,
  onBeforeUnmount,
} from "vue";
import { useUserStore } from "@/stores/useUserStore";
import api from "@/services/api";
import { useGroupStore } from "@/stores/useGroupStore";
import AddGroupForm from "../Admin/AddGroupForm.vue";
import Multiselect from "vue-multiselect";

// ====================================================================
// PROPS
// ====================================================================
const props = defineProps({
  userId: {
    type: Number,
    required: true,
  },
});

const userStore = useUserStore();
const groupStore = useGroupStore();
const emit = defineEmits(["close", "refresh"]);

// ====================================================================
// ESTADO DO FORMULÁRIO
// ====================================================================

const identity = ref({
  userName: "",
  email: "",
  password: "",
  fullName: "",
  birthDate: "",
});

const account = ref({
  status: "Active",
  onboardingComplete: false,
  isDeleted: false,
  deactivatedAt: "",
  purgeAt: "",
});

const profile = ref({
  bio: "",
  profession: "",
  country: "",
  photoPath: "",
});

const selectedFile = ref(null);

const avatarSrc = computed(() => {
  const raw = profile.value.photoPath || "";
  if (!raw) return "http://localhost:5196/userphotos/avatar_colorido.png";
  if (raw.startsWith("http") || raw.startsWith("blob:")) return raw;
  let base = (api.defaults.baseURL || "")
    .replace(/\/api$/, "")
    .replace(/\/+$/, "");
  return base + (raw.startsWith("/") ? raw : "/" + raw);
});

function handleFileChange(event) {
  const file = event.target.files[0];
  if (!file) return;
  selectedFile.value = file;
  profile.value.photoPath = URL.createObjectURL(file);
}

const contact = ref({
  phoneE164: "",
  phoneIsWhatsapp: false,
  isWhatsappPublic: false,
  telegramUsername: "",
  instagramUsername: "",
  linkedInUsername: "",
  facebookUsername: "",
});

const security = ref({
  lastIpAddress: "",
  ipLocation: "",
});

// Integrações / billing
const billing = ref({
  mpCustomerId: "",
  // ── PATCH: saldo de crédito da conta ─────────────────────────────────────
  creditBalance: 0,
  // ── FIM PATCH ─────────────────────────────────────────────────────────────
});

const counters = ref({
  groupUserCount: 0,
  activeNotesCount: 0,
  activeTextsCount: 0,
});

// ====================================================================
// HELPER: converte string ISO (com ou sem timezone) → "YYYY-MM-DDTHH:mm"
// para compatibilidade com input[type=datetime-local]
// ====================================================================
function toDatetimeLocal(val) {
  if (!val) return "";
  // Remove sufixo Z ou offset (+00:00) e trunca nos primeiros 16 chars
  // Ex.: "2024-03-15T10:30:00" → "2024-03-15T10:30"
  //      "2024-03-15T10:30:00.000Z" → "2024-03-15T10:30"
  return String(val).replace(/(\.\d+)?Z$/, "").replace(/([+-]\d{2}:\d{2})$/, "").substring(0, 16);
}

function toDateOnly(val) {
  if (!val) return "";
  return String(val).substring(0, 10); // "2000-01-01T00:00:00" → "2000-01-01"
}

// ====================================================================
// CONTROLE DE UI
// ====================================================================
const errors = ref({});
const isSubmitting = ref(false);
const userNameInputRef = ref(null);
const isLoading = ref(true);

const openSections = ref({
  identity: true,
  profile: false,
  contact: false,
  account: false,
  groups: false,
  counters: false,
  billing: false,
  security: false,
});

function toggleSection(key) {
  openSections.value[key] = !openSections.value[key];
}

// ====================================================================
// GRUPOS – multiselect com criação inline
// ====================================================================
const selectedGroups = ref([]);
const showAddGroupModal = ref(false);
const newGroupName = ref("");
const multiselectRef = ref(null);
const allGroups = ref([]);

const groupList = computed(() =>
  allGroups.value
    .filter((g) => g.groupName.toLowerCase() !== "todos")
    .map((g) => ({
      label: g.groupName,
      value: g.groupId,
    }))
);

function onMultiselectOpen() {
  nextTick(() => {
    if (!multiselectRef.value) return;
    const el = multiselectRef.value.$el;
    const rect = el.getBoundingClientRect();
    const wrapper = el.querySelector(".multiselect__content-wrapper");
    if (!wrapper) return;
    wrapper.style.position = "fixed";
    wrapper.style.width = rect.width + "px";
    wrapper.style.left = rect.left + "px";
    wrapper.style.top = rect.bottom + "px";
    wrapper.style.zIndex = "9999";
  });
}

function onMultiselectClose() {
  if (!multiselectRef.value) return;
  const wrapper = multiselectRef.value.$el.querySelector(
    ".multiselect__content-wrapper"
  );
  if (wrapper) wrapper.style.position = "";
}

function addNewGroup(newTag) {
  const trimmedName = newTag.trim();
  if (!trimmedName) return;

  const existingGroup = groupStore.groups.find(
    (g) => g.groupName.toLowerCase() === trimmedName.toLowerCase()
  );
  if (existingGroup) {
    const isSelected = selectedGroups.value.some(
      (sg) => sg.value === existingGroup.groupId
    );
    if (!isSelected) {
      selectedGroups.value.push({
        label: existingGroup.groupName,
        value: existingGroup.groupId,
      });
    }
    return;
  }

  newGroupName.value = trimmedName;
  showAddGroupModal.value = true;
}

async function handleGroupCreated() {
  showAddGroupModal.value = false;
  try {
    await groupStore.fetchGroups();
    allGroups.value = [...groupStore.groups];
  } catch (err) {
    console.error("[EditUserForm] falha ao recarregar grupos:", err);
  }

  const newGroup = allGroups.value.find(
    (g) => g.groupName === newGroupName.value
  );
  if (newGroup) {
    selectedGroups.value = [
      ...selectedGroups.value,
      { label: newGroup.groupName, value: newGroup.groupId },
    ];
  }
  newGroupName.value = "";
}

// ====================================================================
// TOAST
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

// ====================================================================
// LIFECYCLE — carrega usuário e grupos
// ====================================================================
onMounted(async () => {
  document.addEventListener("keydown", handleEscKey);

  try {
    // ── FIX GRUPOS: carrega lista completa de grupos ANTES de mapear os do usuário
    await groupStore.fetchGroups();
    allGroups.value = [...groupStore.groups];

    // ── Busca dados completos do usuário (inclui groups, plan, roles)
    const user = await userStore.getUserById(props.userId);

    // ── Identidade
    identity.value = {
      userName:  user.userName  ?? "",
      email:     user.email     ?? "",
      password:  "",
      fullName:  user.fullName  ?? "",
      // FIX DATAS: usa helper para garantir formato correto para input[type=date]
      birthDate: toDateOnly(user.birthDate),
    };

    // ── Conta e ciclo de vida
    // FIX isDeleted: o campo chega como user.isDeleted via spread do _normalizeUser
    // FIX datas: toDatetimeLocal converte ISO → "YYYY-MM-DDTHH:mm" para datetime-local
    account.value = {
      status:             user.statusLabel ?? "Active",
      onboardingComplete: !!user.onboardingComplete,
      isDeleted:          !!user.isDeleted,
      deactivatedAt:      toDatetimeLocal(user.deactivatedAt),
      purgeAt:            toDatetimeLocal(user.purgeAt),
    };

    // ── Perfil
    profile.value = {
      bio:        user.bio        ?? "",
      profession: user.profession ?? "",
      country:    user.country    ?? "",
      photoPath:  user.photoUrl   ?? user.photoPath ?? "",
    };

    // ── Contato
    contact.value = {
      phoneE164:         user.phoneE164        ?? "",
      phoneIsWhatsapp:   !!user.phoneIsWhatsapp,
      isWhatsappPublic:  !!user.isWhatsappPublic,
      telegramUsername:  user.telegramUsername  ?? "",
      instagramUsername: user.instagramUsername ?? "",
      linkedInUsername:  user.linkedInUsername  ?? "",
      facebookUsername:  user.facebookUsername  ?? "",
    };

    // ── Segurança / IP
    security.value = {
      lastIpAddress: user.lastIpAddress ?? "",
      ipLocation:    user.ipLocation    ?? "",
    };

    // ── Billing
    billing.value = {
      mpCustomerId: user.mpCustomerId ?? "",
      // ── PATCH: lê creditBalance normalizado pelo _normalizeUser da store ──
      creditBalance: user.creditBalance ?? 0,
      // ── FIM PATCH ───────────────────────────────────────────────────────────
    };

    // ── Contadores
    counters.value = {
      groupUserCount:   user.groupUserCount   ?? 0,
      activeNotesCount: user.activeNotesCount ?? 0,
      activeTextsCount: user.activeTextsCount ?? 0,
    };

    // ── FIX GRUPOS: user.groups vem como [{groupId, groupName, description}]
    // Cruza com allGroups para garantir que label/value batem com groupList
    const rawGroups = user.groups ?? [];
    selectedGroups.value = rawGroups
      .filter((g) => g.groupName?.toLowerCase() !== "todos")
      .map((g) => {
        // Preferência: achar na lista carregada (garante consistência de IDs)
        const match = allGroups.value.find(
          (ag) => ag.groupId === (g.groupId ?? g.value)
        );
        return {
          label: match?.groupName ?? g.groupName ?? g.label ?? "",
          value: match?.groupId   ?? g.groupId   ?? g.value,
        };
      })
      .filter((g) => g.value != null);

  } catch (err) {
    console.error("[EditUserForm] erro ao carregar usuário:", err);
    showErrorToast("Não foi possível carregar os dados do usuário.");
  } finally {
    isLoading.value = false;
    await nextTick();
    if (userNameInputRef.value) userNameInputRef.value.focus();
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});

function handleEscKey(event) {
  if (event.key === "Escape") closeModal();
}

// ====================================================================
// REGRA: desmarca isWhatsappPublic quando phoneIsWhatsapp for desmarcado
// ====================================================================
watch(
  () => contact.value.phoneIsWhatsapp,
  (isWhatsapp) => {
    if (!isWhatsapp) contact.value.isWhatsappPublic = false;
  }
);

// ====================================================================
// VALIDAÇÃO
// ====================================================================
function validateForm() {
  errors.value = {};

  if (
    !identity.value.userName ||
    identity.value.userName.trim().length < 3 ||
    identity.value.userName.trim().length > 128
  ) {
    errors.value.userName = "O nome de usuário deve ter entre 3 e 128 caracteres.";
  }

  if (!identity.value.email || !/^\S+@\S+\.\S+$/.test(identity.value.email)) {
    errors.value.email = "Por favor, insira um e-mail válido.";
  }

  if (identity.value.password) {
    const pwd = identity.value.password;
    if (pwd.length < 8 || !/[a-zA-Z]/.test(pwd) || !/\d/.test(pwd)) {
      errors.value.password =
        "A senha deve ter no mínimo 8 caracteres, com pelo menos uma letra e um número.";
    }
  }

  if (
    contact.value.phoneE164 &&
    !/^\+[1-9]\d{11,14}$/.test(contact.value.phoneE164)
  ) {
    errors.value.phoneE164 =
      "Telefone inválido. Use o padrão E.164 (ex: +5581998765432).";
  }

  return Object.keys(errors.value).length === 0;
}

// ====================================================================
// SUBMIT — sempre FormData
// ====================================================================
function buildFormData() {
  const fd = new FormData();

  fd.append("userId",             String(props.userId));
  fd.append("userName",           identity.value.userName.trim());
  fd.append("email",              identity.value.email.trim());
  fd.append("fullName",           identity.value.fullName?.trim() || "");
  fd.append("birthDate",          identity.value.birthDate || "");

  fd.append("statusString",       account.value.status || "Active");
  fd.append("onboardingComplete", String(!!account.value.onboardingComplete));
  fd.append("isDeleted",          String(!!account.value.isDeleted));
  fd.append("deactivatedAt",      account.value.deactivatedAt || "");
  fd.append("purgeAt",            account.value.purgeAt       || "");

  fd.append("bio",        profile.value.bio        || "");
  fd.append("profession", profile.value.profession || "");
  fd.append("country",    profile.value.country    || "");

  if (selectedFile.value) {
    fd.append("photoFile", selectedFile.value);
  } else {
    fd.append("photoPath", profile.value.photoPath || "");
  }

  fd.append("phoneE164",         contact.value.phoneE164        || "");
  fd.append("phoneIsWhatsapp",   String(contact.value.phoneIsWhatsapp));
  fd.append("isWhatsappPublic",  String(contact.value.isWhatsappPublic));
  fd.append("telegramUsername",  contact.value.telegramUsername  || "");
  fd.append("instagramUsername", contact.value.instagramUsername || "");
  fd.append("linkedInUsername",  contact.value.linkedInUsername  || "");
  fd.append("facebookUsername",  contact.value.facebookUsername  || "");

  if (identity.value.password)
    fd.append("password", identity.value.password);

  fd.append("lastIpAddress", security.value.lastIpAddress || "");
  fd.append("ipLocation",    security.value.ipLocation    || "");

  // Billing
  if (billing.value.mpCustomerId)
    fd.append("mpCustomerId", billing.value.mpCustomerId);
  // ── PATCH: saldo de crédito ───────────────────────────────────────────────
  fd.append("creditBalance", String(billing.value.creditBalance ?? 0));
  // ── FIM PATCH ─────────────────────────────────────────────────────────────

  // Grupos: um append por ID
  selectedGroups.value.forEach((g) => fd.append("groupIds", String(g.value)));

  fd.append("groupUserCount",   String(counters.value.groupUserCount));
  fd.append("activeNotesCount", String(counters.value.activeNotesCount));
  fd.append("activeTextsCount", String(counters.value.activeTextsCount));

  return fd;
}

async function handleSubmit() {
  if (isSubmitting.value || isLoading.value) return;

  if (!validateForm()) {
    showErrorToast("Verifique os campos obrigatórios antes de salvar.");
    return;
  }

  isSubmitting.value = true;

  try {
    await userStore.updateUser(props.userId, buildFormData());
    showSuccessToast("Usuário atualizado com sucesso.");
    setTimeout(() => {
      emit("refresh");
      emit("close");
      isSubmitting.value = false;
    }, 2400);
  } catch (error) {
    console.error("❌ Erro no formulário de edição de usuário:", error);
    let msg = "Ocorreu um erro ao atualizar o usuário.";
    const responseData = error?.response?.data;
    if (responseData?.errors) {
      const msgs = Object.values(responseData.errors).flat().filter(Boolean);
      if (msgs.length > 0) msg = msgs.join("\n");
    } else if (responseData?.message) {
      msg = responseData.message;
    } else if (error?.message) {
      msg = error.message;
    }
    showErrorToast(msg);
    isSubmitting.value = false;
  }
}

// ====================================================================
// FECHAR MODAL
// ====================================================================
function closeModal() {
  if (!isSubmitting.value) {
    userStore.clearStoreError();
    emit("close");
  }
}

// ====================================================================
// STATUS OPTIONS
// ====================================================================
const statusOptions = [
  { value: "Pending",   label: "Pendente"  },
  { value: "Active",    label: "Ativo"     },
  { value: "Inactive",  label: "Inativo"   },
  { value: "Blocked", label: "Bloqueado"  },
];
</script>

<template>
  <div class="modal-backdrop" @click.self="!isSubmitting">
    <div class="modal-panel">

      <div class="modal-header">
        <h2 class="modal-title">Editar Usuário</h2>
        <button
          @click="closeModal"
          class="modal-close-btn"
          :disabled="isSubmitting"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>

      <div v-if="isLoading" class="loading-state">
        <svg class="w-6 h-6 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
        </svg>
        <span>Carregando dados do usuário...</span>
      </div>

      <form v-else @submit.prevent="handleSubmit" novalidate>
        <div class="modal-body">

          <div class="section">
            <button type="button" class="section-toggle" @click="toggleSection('identity')">
              <span class="section-icon">👤</span>
              <span class="section-title">Identidade e Credenciais</span>
              <span class="section-badge required">Obrigatório</span>
              <svg class="chevron" :class="{ open: openSections.identity }" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd"/>
              </svg>
            </button>
            <div v-show="openSections.identity" class="section-body">
              <div class="form-grid-2">
                <div class="form-group">
                  <label class="form-label">Nome de Usuário <span class="required-star">*</span></label>
                  <input ref="userNameInputRef" type="text" v-model="identity.userName" autocomplete="off" class="input-base" :class="{ 'is-invalid': errors.userName }"/>
                  <span v-if="errors.userName" class="error-msg">{{ errors.userName }}</span>
                </div>

                <div class="form-group">
                  <label class="form-label">E-mail <span class="required-star">*</span></label>
                  <input type="email" v-model="identity.email" autocomplete="off" class="input-base" :class="{ 'is-invalid': errors.email }"/>
                  <span v-if="errors.email" class="error-msg">{{ errors.email }}</span>
                </div>

                <div class="form-group">
                  <label class="form-label">Nova Senha <span class="optional-tag">opcional</span></label>
                  <input type="password" v-model="identity.password" autocomplete="new-password" class="input-base" :class="{ 'is-invalid': errors.password }" placeholder="Deixe em branco para manter a atual"/>
                  <span v-if="errors.password" class="error-msg">{{ errors.password }}</span>
                </div>

                <div class="form-group">
                  <label class="form-label">Nome Completo <span class="optional-tag">opcional</span></label>
                  <input type="text" v-model="identity.fullName" class="input-base" placeholder="Ex.: João da Silva"/>
                </div>

                <div class="form-group">
                  <label class="form-label">Data de Nascimento <span class="optional-tag">opcional</span></label>
                  <input type="date" v-model="identity.birthDate" class="input-base"/>
                </div>
              </div>
            </div>
          </div>

          <div class="section">
            <button type="button" class="section-toggle" @click="toggleSection('profile')">
              <span class="section-icon">📝</span>
              <span class="section-title">Perfil Público e Informações Pessoais</span>
              <span class="section-badge optional">Opcional</span>
              <svg class="chevron" :class="{ open: openSections.profile }" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd"/>
              </svg>
            </button>
            <div v-show="openSections.profile" class="section-body">
              <div class="profile-top-row">
                <div class="profile-avatar-col">
                  <label class="form-label">Foto</label>
                  <div class="photo-upload-container">
                    <img :src="avatarSrc" alt="Preview da foto" class="avatar-preview"/>
                    <input id="photo-upload-edit" type="file" accept="image/*" class="hidden" @change="handleFileChange"/>
                    <label for="photo-upload-edit" class="btn btn-secondary btn-sm">
                      {{ selectedFile ? "Trocar foto" : "Alterar foto" }}
                    </label>
                    <span v-if="selectedFile" class="hint" style="font-size:0.78rem;margin-top:0.1rem">{{ selectedFile.name }}</span>
                  </div>
                </div>
                <div class="profile-fields-col">
                  <div class="form-group">
                    <label class="form-label">Profissão</label>
                    <input type="text" v-model="profile.profession" class="input-base" placeholder="Ex.: Escritora, Jornalista..." maxlength="100"/>
                  </div>
                  <div class="form-group">
                    <label class="form-label">País</label>
                    <input type="text" v-model="profile.country" class="input-base" placeholder="Ex.: Brasil" maxlength="45"/>
                  </div>
                </div>
              </div>
              <div class="form-group" style="margin-top:1rem">
                <label class="form-label">Bio</label>
                <textarea v-model="profile.bio" class="input-base textarea" placeholder="Breve apresentação do usuário..." maxlength="600" rows="3"/>
                <span class="char-count">{{ profile.bio.length }}/600</span>
              </div>
            </div>
          </div>

          <div class="section">
            <button type="button" class="section-toggle" @click="toggleSection('contact')">
              <span class="section-icon">📱</span>
              <span class="section-title">Contato e Redes Sociais</span>
              <span class="section-badge optional">Opcional</span>
              <svg class="chevron" :class="{ open: openSections.contact }" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd"/>
              </svg>
            </button>
            <div v-show="openSections.contact" class="section-body">
              <div class="form-grid-2">
                <div class="form-group">
                  <label class="form-label">Telefone (E.164)</label>
                  <input type="tel" v-model="contact.phoneE164" class="input-base" :class="{ 'is-invalid': errors.phoneE164 }" placeholder="+5581998765432" maxlength="16"/>
                  <span v-if="errors.phoneE164" class="error-msg">{{ errors.phoneE164 }}</span>
                </div>
                <div class="form-group">
                  <label class="form-label">Telegram</label>
                  <input type="text" v-model="contact.telegramUsername" class="input-base" placeholder="@usuario" maxlength="50"/>
                </div>
                <div class="form-group">
                  <label class="form-label">Instagram</label>
                  <input type="text" v-model="contact.instagramUsername" class="input-base" placeholder="@usuario" maxlength="50"/>
                </div>
                <div class="form-group">
                  <label class="form-label">LinkedIn</label>
                  <input type="text" v-model="contact.linkedInUsername" class="input-base" placeholder="linkedin.com/in/usuario" maxlength="80"/>
                </div>
                <div class="form-group">
                  <label class="form-label">Facebook</label>
                  <input type="text" v-model="contact.facebookUsername" class="input-base" placeholder="facebook.com/usuario" maxlength="80"/>
                </div>
                <div class="form-group checkbox-row col-span-2">
                  <label class="checkbox-item">
                    <input type="checkbox" v-model="contact.phoneIsWhatsapp" class="checkbox-style"/>
                    <span>Telefone é WhatsApp</span>
                  </label>
                  <label class="checkbox-item" :class="{ disabled: !contact.phoneIsWhatsapp }">
                    <input type="checkbox" v-model="contact.isWhatsappPublic" :disabled="!contact.phoneIsWhatsapp" class="checkbox-style"/>
                    <span>WhatsApp público</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div class="section">
            <button type="button" class="section-toggle" @click="toggleSection('account')">
              <span class="section-icon">🔖</span>
              <span class="section-title">Status de Conta e Ciclo de Vida</span>
              <span class="section-badge required">Obrigatório</span>
              <svg class="chevron" :class="{ open: openSections.account }" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd"/>
              </svg>
            </button>
            <div v-show="openSections.account" class="section-body">
              <div class="form-grid-2">
                <div class="form-group">
                  <label class="form-label">Status <span class="required-star">*</span></label>
                  <select v-model="account.status" class="input-base">
                    <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                  </select>
                </div>

                <div class="form-group">
                  <label class="form-label">Desativado em <span class="optional-tag">opcional</span></label>
                  <input type="datetime-local" v-model="account.deactivatedAt" class="input-base"/>
                </div>

                <div class="form-group">
                  <label class="form-label">Purgar em <span class="optional-tag">opcional</span></label>
                  <input type="datetime-local" v-model="account.purgeAt" class="input-base"/>
                </div>

                <div class="form-group checkbox-row col-span-2">
                  <label class="checkbox-item">
                    <input type="checkbox" v-model="account.onboardingComplete" class="checkbox-style"/>
                    <span>Onboarding Completo</span>
                  </label>
                  <label class="checkbox-item">
                    <input type="checkbox" v-model="account.isDeleted" class="checkbox-style"/>
                    <span>Marcado como Excluído</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div class="section">
            <button type="button" class="section-toggle" @click="toggleSection('groups')">
              <span class="section-icon">👥</span>
              <span class="section-title">Grupos</span>
              <span class="section-badge optional">Opcional</span>
              <svg class="chevron" :class="{ open: openSections.groups }" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd"/>
              </svg>
            </button>
            <div v-show="openSections.groups" class="section-body">
              <div class="form-group">
                <label class="form-label">Grupos</label>
                <Multiselect
                  ref="multiselectRef"
                  v-model="selectedGroups"
                  :options="groupList"
                  :multiple="true"
                  placeholder="Selecione ou crie grupos..."
                  label="label"
                  track-by="value"
                  :taggable="true"
                  :allow-empty="true"
                  :close-on-select="false"
                  :show-labels="false"
                  :clear-on-select="false"
                  @tag="addNewGroup"
                  @open="onMultiselectOpen"
                  @close="onMultiselectClose"
                />
                <span class="hint" style="margin-top:0.25rem">
                  {{ selectedGroups.length }} grupo(s) selecionado(s)
                </span>
              </div>
            </div>
          </div>

          <div class="section">
            <button type="button" class="section-toggle" @click="toggleSection('counters')">
              <span class="section-icon">📊</span>
              <span class="section-title">Contadores</span>
              <span class="section-badge optional">Opcional</span>
              <svg class="chevron" :class="{ open: openSections.counters }" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd"/>
              </svg>
            </button>
            <div v-show="openSections.counters" class="section-body">
              <div class="form-grid-3">
                <div class="form-group">
                  <label class="form-label">GroupUserCount</label>
                  <input type="number" v-model.number="counters.groupUserCount" class="input-base" min="0"/>
                </div>
                <div class="form-group">
                  <label class="form-label">ActiveNotesCount</label>
                  <input type="number" v-model.number="counters.activeNotesCount" class="input-base" min="0"/>
                </div>
                <div class="form-group">
                  <label class="form-label">ActiveTextsCount</label>
                  <input type="number" v-model.number="counters.activeTextsCount" class="input-base" min="0"/>
                </div>
              </div>
            </div>
          </div>

          <div class="section">
            <button type="button" class="section-toggle" @click="toggleSection('billing')">
              <span class="section-icon">💳</span>
              <span class="section-title">Billing / Integração</span>
              <span class="section-badge optional">Opcional</span>
              <svg class="chevron" :class="{ open: openSections.billing }" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd"/>
              </svg>
            </button>
            <div v-show="openSections.billing" class="section-body">
              <div class="form-group">
                <label class="form-label">
                  MpCustomerId
                  <span class="optional-tag">opcional</span>
                </label>
                <input
                  type="text"
                  v-model="billing.mpCustomerId"
                  class="input-base"
                  placeholder="ID do cliente no Mercado Pago"
                  maxlength="255"
                />
              </div>
              <!-- ── PATCH: campo creditBalance ───────────────────────────── -->
              <div class="form-group" style="margin-top: 1rem;">
                <label class="form-label">
                  Saldo de Crédito
                  <span class="optional-tag">opcional</span>
                </label>
                <input
                  type="number"
                  v-model.number="billing.creditBalance"
                  class="input-base"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
              <!-- ── FIM PATCH ─────────────────────────────────────────────── -->
            </div>
          </div>

          <div class="section">
            <button type="button" class="section-toggle" @click="toggleSection('security')">
              <span class="section-icon">🌐</span>
              <span class="section-title">IP e Localização</span>
              <span class="section-badge optional">Opcional</span>
              <svg class="chevron" :class="{ open: openSections.security }" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd"/>
              </svg>
            </button>
            <div v-show="openSections.security" class="section-body">
              <div class="info-banner">
                <svg class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 110 20A10 10 0 0112 2z"/>
                </svg>
                <span>Preenchido automaticamente pelo backend a cada acesso. Não editável.</span>
              </div>
              <div class="form-grid-2">
                <div class="form-group">
                  <label class="form-label">Último IP</label>
                  <input type="text" :value="security.lastIpAddress" class="input-base input-readonly" readonly tabindex="-1" placeholder="—"/>
                </div>
                <div class="form-group">
                  <label class="form-label">Localização do IP</label>
                  <input type="text" :value="security.ipLocation" class="input-base input-readonly" readonly tabindex="-1" placeholder="—"/>
                </div>
              </div>
            </div>
          </div>

        </div>
        <div class="modal-footer">
          <button type="button" @click="closeModal" class="btn btn-secondary" :disabled="isSubmitting">Cancelar</button>
          <button type="submit" class="btn btn-success" :disabled="isSubmitting">
            <svg v-if="isSubmitting" class="w-5 h-5 mr-2 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
            </svg>
            <svg v-else class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"/>
            </svg>
            <span>{{ isSubmitting ? "Salvando..." : "Salvar Alterações" }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <Teleport to="body">
    <AddGroupForm
      v-if="showAddGroupModal"
      :initial-name="newGroupName"
      @close="showAddGroupModal = false"
      @refresh="handleGroupCreated"
    />
  </Teleport>

  <Teleport to="body">
    <transition name="toast-fade">
      <div v-if="showToast" class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4">
        <div :class="['toast pointer-events-auto', toastType === 'error' ? 'toast-error' : 'toast-success']">
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle"/>
              <path v-if="toastType === 'error'" d="M10 5v6M10 13.5v1.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
              <path v-else d="M7.5 10.5 9.5 12.5 13 8.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">{{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}</p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button type="button" class="toast-close" @click="showToast = false" aria-label="Fechar notificação">x</button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>

.modal-panel {
  max-width: 52rem;
  max-height: calc(100vh - 40px);
}

.modal-panel > form { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; overflow: hidden; }

.modal-header {
  flex-shrink: 0;
}

.loading-state {
  flex: 1 1 auto;
}

.modal-body .section + .section { margin-top: 0.5rem; }

.modal-footer {
  flex-shrink: 0;
}

.section { border: 1px solid #e8edf3; border-radius: 10px; overflow: visible; }

.section-toggle {
  border-radius: 10px 10px 0 0; width: 100%;
  display: flex; align-items: center; gap: 0.5rem;
  padding: 0.625rem 1rem; background-color: var(--surface-2);
  border: none; cursor: pointer; text-align: left; transition: background-color 0.15s;
}
.section-toggle:hover { background-color: var(--surface-3); }

.section-icon  { font-size: 0.95rem; flex-shrink: 0; }
.section-title { font-size: 0.875rem; font-weight: 600; color: var(--text-dark); flex: 1; }

.section-badge {
  font-size: 0.7rem; font-weight: 600; padding: 0.15rem 0.5rem;
  border-radius: 20px; text-transform: uppercase; letter-spacing: 0.04em; flex-shrink: 0;
}
.section-badge.required { background-color: var(--warning-bg); color: var(--warning-text); }
.section-badge.optional  { background-color: #e0f2fe; color: #0369a1; }

.chevron { width: 1rem; height: 1rem; color: var(--text-light); flex-shrink: 0; transition: transform 0.2s ease; }
.chevron.open { transform: rotate(180deg); }

.section-body { padding: 1rem; background-color: var(--surface); }

.form-grid-2 { display: grid; grid-template-columns: 1fr; gap: 1rem; }
.form-grid-3 { display: grid; grid-template-columns: 1fr; gap: 1rem; }

@media (min-width: 640px) {
  .form-grid-2 { grid-template-columns: 1fr 1fr; }
  .form-grid-3 { grid-template-columns: 1fr 1fr 1fr; }
}

.col-span-2 { grid-column: 1 / -1; }

.required-star { color: var(--danger); font-weight: 700; }

.optional-tag {
  font-size: 0.7rem; font-weight: 400; color: var(--text-light);
  background-color: var(--surface-3); padding: 0.1rem 0.4rem; border-radius: 4px;
}

.hint { font-size: 0.72rem; font-weight: 400; color: var(--text-light); }

.input-base.textarea { resize: vertical; min-height: 76px; }
.input-readonly { background-color: var(--surface-2); color: var(--text-subtle); cursor: default; }

select.input-base {
  height: auto;
}
select.input-base option { padding: 0.25rem 0.5rem; font-size: 0.9rem; background-color: var(--surface); color: var(--text-dark); }

.error-msg  { color: var(--danger); font-size: 0.78rem; }
.char-count { font-size: 0.72rem; color: var(--text-light); text-align: right; margin-top: -0.1rem; }

.checkbox-row  { flex-direction: row; flex-wrap: wrap; gap: 1rem; align-items: center; padding-top: 0.25rem; }
.checkbox-item { display: flex; align-items: center; gap: 0.5rem; cursor: pointer; font-size: 0.875rem; color: var(--text-dark); user-select: none; }
.checkbox-item.disabled { opacity: 0.45; cursor: not-allowed; }

.checkbox-style {
  width: 1rem;
}

.info-banner {
  display: flex; align-items: flex-start; gap: 0.5rem;
  padding: 0.625rem 0.875rem; background-color: var(--info-bg);
  border: 1px solid #bfdbfe; border-radius: 8px;
  font-size: 0.8rem; color: var(--link-hover); margin-bottom: 1rem;
}

.profile-top-row   { display: flex; gap: 1.5rem; align-items: flex-start; }
.profile-avatar-col { display: flex; flex-direction: column; gap: 0.25rem; flex-shrink: 0; width: 110px; }
.profile-fields-col { flex: 1; display: flex; flex-direction: column; gap: 1rem; min-width: 0; max-width: 40%; }

@media (max-width: 480px) {
  .profile-top-row    { flex-direction: column; }
  .profile-avatar-col { width: 100%; align-items: center; }
}

.photo-upload-container { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; }
.avatar-preview { width: 84px; height: 84px; border-radius: 50%; object-fit: cover; border: 2px solid var(--neutral-200); }
.hidden { display: none; }
.btn-sm { height: auto; padding: 0.35rem 0.8rem; font-size: 0.84rem; }
</style>

<style scoped>
:deep(.multiselect) {
  width: 100%; border: 1px solid var(--neutral-200); border-radius: 8px;
  background-color: var(--surface); transition: border-color 0.2s, box-shadow 0.2s;
  font-size: 0.9rem; position: relative;
}
:deep(.multiselect:focus-within) { border-color: var(--focus-border); box-shadow: var(--focus-ring); }
:deep(.multiselect__tags) { border: none; background: transparent; padding-top: 3px; padding-bottom: 3px; min-height: 36px; }
:deep(.multiselect__input),
:deep(.multiselect__single) {
  border: none; outline: none !important; box-shadow: none !important;
  background: transparent !important; font-size: 0.9rem; margin: 0; padding: 0; line-height: 1.5;
}
:deep(.multiselect__select) { height: 36px; }
:deep(.multiselect__content-wrapper) {
  z-index: 9999; background: var(--surface); border: 1px solid var(--neutral-200); border-top: none;
  border-radius: 0 0 8px 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  max-height: 200px; overflow-y: auto;
}
:deep(.multiselect__option)            { color: var(--text-dark); font-size: 0.9rem; }
:deep(.multiselect__option--highlight) { background-color: var(--focus-border); color: var(--text-on-brand); }
:deep(.multiselect__tag)               { background-color: var(--brand-light); color: var(--text-on-brand); font-size: 0.82rem; }
:deep(.multiselect__tag-icon:hover)    { background-color: var(--brand-light-hover); }
:deep(.multiselect__placeholder)       { color: var(--text-subtle); font-size: 0.9rem; padding: 0; margin: 0; }
</style>